/**
 * A run of consecutive build steps folded into one line, in plain words. While
 * running it reads as the current step ("Creating the home page"); once done it
 * is a summary ("Created 3 pages · Made 4 edits"). Collapsed by default — click
 * for each step.
 */
import { useState } from 'react';
import type { ToolPart } from '~/components/chat/message-parts';
import { plainStep, plainSummary } from './plain-steps';
import { ToolLine } from './ToolLine';
import { DuneaSpinner } from './DuneaSpinner';

export const LIVE_WINDOW = 5;

const isPending = (p: ToolPart) => p.state !== 'output-available' && p.state !== 'output-error';

export function ToolGroup({ parts, isStreaming = false }: { parts: ToolPart[]; isStreaming?: boolean }) {
  const running = isStreaming && parts.some(isPending);
  const [expanded, setExpanded] = useState(false);
  const current = running ? (parts.find(isPending) ?? parts[parts.length - 1]) : undefined;
  const title = current ? plainStep(current, false).text : plainSummary(parts) || `${parts.length} steps`;

  return (
    <div className="my-1">
      <div
        className="group/tg flex items-center gap-2 py-[3px] px-1.5 -mx-1.5 rounded-md cursor-pointer select-none transition-colors hover:bg-white/[0.025]"
        onClick={() => setExpanded(!expanded)}
        title={expanded ? 'Hide the steps' : 'Show each step'}
      >
        {running ? (
          <DuneaSpinner size={13} className="text-accent flex-shrink-0" />
        ) : (
          <span className="i-ph:check text-[13px] text-faint flex-shrink-0" />
        )}
        <span className={`text-[13.5px] truncate min-w-0 ${running ? 'text-secondary ui-pulsing' : 'text-muted'}`}>
          {title}
        </span>
        <span
          className={`i-ph:caret-right text-[11px] text-faint flex-shrink-0 transition-transform ${
            expanded ? 'rotate-90' : 'opacity-0 group-hover/tg:opacity-100 touch:opacity-100'
          }`}
        />
      </div>
      {expanded && (
        <div className="ml-[6px] pl-3.5 border-l border-hairline">
          {parts.map((p) => (
            <ToolLine key={p.toolCallId} part={p} isStreaming={isStreaming} />
          ))}
        </div>
      )}
    </div>
  );
}
